import { matchSorter } from "match-sorter";
import { useMemo, useState } from "react";
import { SearchInput } from "@/core/components/search-input";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import { dayjs } from "@/core/utils/date";
import { createDefaults } from "@/core/utils/loading";
import {
  TextMessageListItem,
  TextMessageListItemSkeleton,
} from "@/text-messages/components/text-message-list-item";
import { usePrefetchCustomerTextMessages } from "@/text-messages/query";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";

const LOADING_TEXT_MESSAGES = createDefaults("placeholder-text-message", 5);

interface TextMessageListProps {
  textMessages?: IMostRecentTextMessage[];
  isLoading?: boolean;
  emptyMessage?: string;
}

export const TextMessageList = ({
  textMessages = [],
  isLoading = false,
  emptyMessage = "No messages available",
}: TextMessageListProps) => {
  const [search, setSearch] = useState("");

  const {
    updateSidebar,
    state: { selectedItemId },
  } = useBusinessSettingsSidebar();

  const handleSelectTextMessage = (textMessage: IMostRecentTextMessage) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.textMessages,
      mode: SIDEBAR_MODES.view,
      selectedItemId: textMessage.id,
      isOpen: true,
    });
  };

  const prefetchCustomerTextMessages = usePrefetchCustomerTextMessages();

  const { schedule: schedulePrefetch, cancel: cancelPrefetch } =
    useDelayedAction(prefetchCustomerTextMessages, 250);

  const filteredTextMessages = useMemo(() => {
    if (!search) return textMessages;
    return matchSorter(textMessages, search, {
      keys: [
        (item) => item.customer?.name || "",
        (item) => item.customer?.phone_number || "",
        (item) => item.body || "",
      ],
      threshold: matchSorter.rankings.CONTAINS,
    });
  }, [textMessages, search]);

  const sortedTextMessages = useMemo(
    () =>
      [...filteredTextMessages].sort(
        (a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf(),
      ),
    [filteredTextMessages],
  );

  return (
    <Stack className="h-full overflow-hidden" gap="xs" fullWidth>
      <SearchInput
        placeholder="Search messages..."
        value={search}
        onChange={(value) => setSearch(value)}
      />
      <ScrollArea className="h-full w-full pr-2">
        <Stack gap="none" className="py-4 pr-4" fullWidth>
          {sortedTextMessages.length === 0 && !isLoading && (
            <Stack
              className="h-80 py-8"
              align="center"
              justify="center"
              fullWidth
            >
              <Typography variant="bodyW1" textColor="secondary">
                {search.trim() ? "No messages match your search" : emptyMessage}
              </Typography>
            </Stack>
          )}
          {isLoading &&
            LOADING_TEXT_MESSAGES.map((t, index) => (
              <TextMessageListItemSkeleton key={t.id} index={index} />
            ))}
          {sortedTextMessages?.map((t) => (
            <TextMessageListItem
              key={t.id}
              textMessage={t}
              isSelected={selectedItemId === t.id}
              onSelect={handleSelectTextMessage}
              onMouseEnter={() => schedulePrefetch(t.customer.id)}
              onMouseLeave={cancelPrefetch}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
